import type { PaginatedInfiniteQueryResult } from "@/lib/common/PaginatedInfiniteQueryResult";
import { useEffect, type RefObject } from "react";

export const useInfiniteScroll = <T,>(ref: RefObject<HTMLElement | null>, forQuery: PaginatedInfiniteQueryResult<T>, scrollRef?: RefObject<HTMLElement | null>): boolean => {
	useEffect(() => {
		let hasCalledForNextPage = false;

		const scrollHandler = () => {
			if (!ref.current || forQuery.isFetchingNextPage || hasCalledForNextPage) {
				return;
			}

			const elementBounds = ref.current.getBoundingClientRect();
			const portionVisible = Math.min(window.innerHeight - elementBounds.y, elementBounds.height) / elementBounds.height;
			const isVisible = portionVisible > 0.2;

			if (isVisible && forQuery.hasNextPage) {
				hasCalledForNextPage = true;
				forQuery.fetchNextPage();
			}
		};

		const target = scrollRef?.current ?? window;
		target.addEventListener("scroll", scrollHandler);
		scrollHandler();

		return () => target.removeEventListener("scroll", scrollHandler);
	}, [forQuery.hasNextPage, forQuery.fetchNextPage, forQuery.isFetchingNextPage, forQuery.data]);

	return forQuery.hasNextPage || forQuery.isLoading;
};
